"use client";

/**
 * P2-06g · Accessible testimonials carousel (client).
 *
 * A snap-scroll track (one card per view on mobile, more on wider screens) with
 * labelled prev/next controls, arrow-key navigation, a polite live region for
 * the current slide, and — only when `autoplay` is set AND motion is allowed —
 * auto-advance with a pause/play toggle. No carousel dependency.
 */
import { useEffect, useRef, useState } from "react";

import { TestimonialCard, type TestimonialView } from "./testimonial-card";
import { useTranslator } from "~/i18n/provider";

const AUTOPLAY_MS = 5000;

export function TestimonialsCarousel({
  items,
  label,
  autoplay,
}: {
  items: TestimonialView[];
  label: string;
  autoplay: boolean;
}) {
  const t = useTranslator();
  const n = items.length;
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);
  const trackRef = useRef<HTMLDivElement>(null);

  const go = (next: number) => setIndex(((next % n) + n) % n);

  // Scroll the track to the active slide when the index changes.
  useEffect(() => {
    const track = trackRef.current;
    const slide = track?.children[index] as HTMLElement | undefined;
    if (track && slide) {
      track.scrollTo({
        left: slide.offsetLeft - track.offsetLeft,
        behavior: "smooth",
      });
    }
  }, [index]);

  // Autoplay only when requested and the user hasn't asked for reduced motion.
  useEffect(() => {
    if (!autoplay) return;
    if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return;
    setPlaying(true);
  }, [autoplay]);

  useEffect(() => {
    if (!playing || n < 2) return;
    const id = setInterval(() => setIndex((i) => (i + 1) % n), AUTOPLAY_MS);
    return () => clearInterval(id);
  }, [playing, n]);

  return (
    <div
      role="region"
      aria-roledescription="carousel"
      aria-label={label}
      onKeyDown={(e) => {
        if (e.key === "ArrowRight") {
          e.preventDefault();
          go(index + 1);
        } else if (e.key === "ArrowLeft") {
          e.preventDefault();
          go(index - 1);
        }
      }}
    >
      <div
        ref={trackRef}
        className="flex snap-x snap-mandatory gap-4 overflow-x-auto pb-2 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      >
        {items.map((item, i) => (
          <div
            key={i}
            role="group"
            aria-roledescription="slide"
            aria-label={`${i + 1} of ${n}`}
            className="w-full shrink-0 snap-start sm:w-[calc(50%-0.5rem)] lg:w-[calc(33.333%-0.667rem)]"
          >
            <TestimonialCard item={item} />
          </div>
        ))}
      </div>

      <div className="mt-5 flex items-center justify-center gap-2">
        <CarouselButton
          label={t("blocks.previousTestimonial")}
          onClick={() => go(index - 1)}
        >
          <path d="M12 4l-6 6 6 6" />
        </CarouselButton>
        {autoplay && (
          <button
            type="button"
            aria-label={playing ? "Pause autoplay" : "Play autoplay"}
            onClick={() => setPlaying((p) => !p)}
            className="inline-flex h-9 items-center rounded-full border border-border bg-surface px-4 text-sm font-medium text-fg transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
          >
            {playing ? "Pause" : "Play"}
          </button>
        )}
        <CarouselButton
          label={t("blocks.nextTestimonial")}
          onClick={() => go(index + 1)}
        >
          <path d="M8 4l6 6-6 6" />
        </CarouselButton>
      </div>

      <p aria-live="polite" className="sr-only">
        Slide {index + 1} of {n}
      </p>
    </div>
  );
}

function CarouselButton({
  label,
  onClick,
  children,
}: {
  label: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <button
      type="button"
      aria-label={label}
      onClick={onClick}
      className="inline-flex size-9 items-center justify-center rounded-full border border-border bg-surface text-fg transition-colors hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
    >
      <svg
        viewBox="0 0 20 20"
        className="size-5"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden
      >
        {children}
      </svg>
    </button>
  );
}
